/**
 * API Service Module
 *
 * Centralized API communication for the invoice generator.
 * Handles all HTTP requests with proper error handling.
 * Uses our own JWT (stored in lib/auth.js) for authentication.
 */

import { API_URL, API_ENDPOINTS, API_TIMEOUT } from '../utils/constants';
import {
  getAccessToken,
  refreshTokens,
  authInitPromise,
} from '../lib/auth';

// ============================================================================
// AUTHENTICATION HEADERS
// ============================================================================

/**
 * Get auth headers with the current JWT access token.
 * Throws if there is no token (use for auth-required endpoints).
 */
// eslint-disable-next-line no-unused-vars
function getAuthHeaders(additionalHeaders = {}) {
  const token = getAccessToken();
  if (!token) {
    throw new ApiError('Not authenticated', 401, 'auth');
  }
  return {
    'Authorization': `Bearer ${token}`,
    ...additionalHeaders,
  };
}

/**
 * Get auth headers when authenticated, or plain headers for guests.
 * Use this for endpoints that work with or without a token.
 */
function getOptionalAuthHeaders(additionalHeaders = {}) {
  const token = getAccessToken();
  return token
    ? { 'Authorization': `Bearer ${token}`, ...additionalHeaders }
    : { ...additionalHeaders };
}

/**
 * Fetch tenant entitlements from backend (Feature #15).
 * 
 * Returns the enabled/disabled status of features for the current tenant.
 * Frontend uses this to hide/disable UI controls for gated features.
 * 
 * Features:
 * - INVOICE_HISTORY: Access to invoice history list and detail views
 * - INVOICE_PDF_DOWNLOAD: Ability to download PDF from history
 * - INVOICE_EXPORT_CSV: Ability to export invoice history as CSV
 * 
 * @returns {Promise<Object>} Entitlements response: { tenant_id, features }
 * @throws {ApiError} On API failure or missing tenant header
 * 
 * @example
 * const { features } = await fetchEntitlements();
 * if (features.INVOICE_EXPORT_CSV) {
 *   // Show export button
 * }
 */
export async function fetchEntitlements() {
  const endpoint = API_ENDPOINTS.ENTITLEMENTS;

  try {
    const response = await authFetch(`${API_URL}${endpoint}`, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
      signal: AbortSignal.timeout(API_TIMEOUT),
    });

    await handleResponse(response, endpoint);
    return await response.json();
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(error.message, null, endpoint);
  }
}

// ============================================================================
// ERROR HANDLING
// ============================================================================

/**
 * Custom API error class with additional context
 */
export class ApiError extends Error {
  constructor(message, status, endpoint, detail = null) {
    super(typeof message === 'string' ? message : JSON.stringify(message));
    this.name = 'ApiError';
    this.status = status;
    this.endpoint = endpoint;
    this.detail = detail;  // raw detail object from backend (may be string or object)
  }
}

// ============================================================================
// AUTHENTICATED FETCH WITH TOKEN REFRESH
// ============================================================================

/**
 * Authenticated fetch that auto-retries once on 401 by refreshing the token.
 *
 * On page load it waits for AuthContext.initAuth to finish (authInitPromise)
 * before deciding whether a token exists, so it never races with the startup
 * refresh for the stored refresh token.
 *
 * All refresh attempts go through the shared refreshTokens() singleton in
 * lib/auth.js, which deduplicates concurrent calls and is the only place
 * that talks to /api/auth/refresh.
 */
async function authFetch(url, options = {}) {
  // If no token is in memory, wait for initAuth to complete before deciding
  // whether to attempt a refresh. This prevents the startup race where both
  // authFetch and initAuth would consume the same one-time refresh token.
  if (!getAccessToken()) {
    await authInitPromise;
  }

  // Still no token after init → not logged in
  if (!getAccessToken()) {
    throw new ApiError('Not authenticated', 401, url);
  }

  const makeRequest = () => {
    const token = getAccessToken();
    return fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
  };

  let response = await makeRequest();

  // On 401, attempt one token refresh then retry the original request.
  if (response.status === 401) {
    const data = await refreshTokens();
    if (data) {
      // Got a fresh access token — retry the original request.
      response = await makeRequest();
    } else {
      // Refresh token is also expired/invalid. Throw a clear session error
      // so the UI can prompt the user to sign in again, rather than
      // surfacing a raw "Invalid or expired token" JSON body.
      throw new ApiError(
        'Your session has expired. Please sign in again.',
        401,
        url,
      );
    }
  }

  return response;
}

// ============================================================================
// ERROR HANDLING
// ============================================================================

/**
 * Handle API response and throw on error
 * @param {Response} response - Fetch response
 * @param {string} endpoint - API endpoint for error context
 * @returns {Response} Original response if OK
 */
async function handleResponse(response, endpoint) {
  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}`;
    let detail = null;

    try {
      const errorData = await response.json();
      detail = errorData.detail ?? null;
      // detail may be a string or an object (e.g. {error, used, limit} for 402)
      errorMessage = typeof detail === 'string'
        ? detail
        : (errorData.message || errorMessage);
    } catch {
      errorMessage = response.statusText || errorMessage;
    }

    throw new ApiError(errorMessage, response.status, endpoint, detail);
  }

  return response;
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

/**
 * @deprecated Use finalizeInvoice() instead which validates, calculates, assigns invoice number,
 * and generates PDF in a single atomic transaction.
 * 
 * Generate an invoice PDF from the backend (LEGACY)
 * 
 * @param {Object} invoiceData - Complete invoice data object
 * @returns {Promise<Blob>} PDF blob for download
 * @throws {ApiError} On API failure
 * 
 * @example
 * // DEPRECATED - use finalizeInvoice instead:
 * // const blob = await finalizeInvoice(formData);
 * const blob = await generateInvoicePDF(formData);
 * downloadPDF(blob, 'invoice.pdf');
 */
export async function generateInvoicePDF_LEGACY(invoiceData) {
  void invoiceData;
  console.warn('generateInvoicePDF_LEGACY is deprecated. Use finalizeInvoice() instead.');
  throw new ApiError(
    'generateInvoicePDF_LEGACY is disabled. Use finalizeInvoice() instead.',
    0,
    'generateInvoicePDF_LEGACY'
  );
}

/**
 * Download a blob as a file
 * 
 * @param {Blob} blob - File blob to download
 * @param {string} filename - Desired filename
 */
export function downloadPDF(blob, filename) {
  if (!blob || !(blob instanceof Blob)) {
    console.error('Invalid blob provided for download');
    return;
  }
  
  const url = window.URL.createObjectURL(blob);
  
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'invoice.pdf';
    link.style.display = 'none';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    // Clean up object URL
    window.URL.revokeObjectURL(url);
  }
}

/**
 * Fetch tax rates from API
 * 
 * @returns {Promise<Object|null>} Tax rates object or null on failure
 */
export async function getTaxRates() {
  const endpoint = API_ENDPOINTS.TAX_RATES;

  try {
    const response = await fetch(`${API_URL}${endpoint}`, {
      headers: getOptionalAuthHeaders(),
      signal: AbortSignal.timeout(API_TIMEOUT),
    });
    
    await handleResponse(response, endpoint);
    
    return await response.json();
  } catch (error) {
    console.error('Failed to fetch tax rates:', error.message);
    return null;
  }
}

/**
 * Fetch currencies from API
 * 
 * @returns {Promise<Object|null>} Currencies object or null on failure
 */
export async function getCurrencies() {
  const endpoint = API_ENDPOINTS.CURRENCIES;

  try {
    const response = await fetch(`${API_URL}${endpoint}`, {
      headers: getOptionalAuthHeaders(),
      signal: AbortSignal.timeout(API_TIMEOUT),
    });
    
    await handleResponse(response, endpoint);
    
    return await response.json();
  } catch (error) {
    console.error('Failed to fetch currencies:', error.message);
    return null;
  }
}

/**
 * Fetch invoice type configuration from backend
 * 
 * Backend is the SINGLE SOURCE OF TRUTH for invoice behavior.
 * This config controls: enabled_fields, required_fields, tax_rules, labels, currency.
 * 
 * @param {string} invoiceType - Invoice type ID (freelancer, coach, gst, international)
 * @returns {Promise<Object>} Invoice type configuration
 * @throws {ApiError} On API failure or invalid type
 */
export async function fetchInvoiceTypeConfig(invoiceType) {
  const endpoint = `${API_ENDPOINTS.INVOICE_TYPE}${invoiceType}`;

  try {
    const response = await fetch(`${API_URL}${endpoint}`, {
      headers: getOptionalAuthHeaders(),
      signal: AbortSignal.timeout(API_TIMEOUT),
    });
    
    await handleResponse(response, endpoint);
    
    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(
      `Failed to fetch config for invoice type: ${invoiceType}`,
      0,
      endpoint
    );
  }
}

/**
 * Fetch all invoice types (for listing/display)
 * 
 * @returns {Promise<Array|null>} Array of invoice types or null on failure
 */
export async function fetchAllInvoiceTypes() {
  const endpoint = API_ENDPOINTS.INVOICE_TYPES;

  try {
    const response = await fetch(`${API_URL}${endpoint}`, {
      headers: getOptionalAuthHeaders(),
      signal: AbortSignal.timeout(API_TIMEOUT),
    });
    
    await handleResponse(response, endpoint);
    
    const data = await response.json();
    return data.invoice_types;
  } catch (error) {
    console.error('Failed to fetch invoice types:', error.message);
    return null;
  }
}

/**
 * Check if the API is available
 * 
 * @returns {Promise<boolean>} Whether API is reachable
 */
/**
 * Fire-and-forget backend pre-warm.
 * Called once on app mount to wake a cold Render free-tier instance before
 * the user reaches any auth or data endpoint.  Never throws.
 */
export async function pingBackend() {
  try {
    await fetch(`${API_URL}${API_ENDPOINTS.HEALTH}`, {
      signal: AbortSignal.timeout(API_TIMEOUT),
    });
  } catch {
    // Intentional: ignore errors — this is best-effort pre-warming only.
  }
}

// ============================================================================
// FEATURE #7: BACKEND-OWNED BUSINESS LOGIC
// ============================================================================

/**
 * Validate invoice draft data against backend configuration
 * 
 * @param {Object} params - Validation parameters
 * @param {string} params.invoice_type - Invoice type ID
 * @param {Object} params.data - Draft invoice data
 * @param {boolean} [params.skip_invoice_number=true] - Skip invoice_number validation
 * @returns {Promise<Object>} Validation response: { valid, errors, schema_version }
 * @throws {ApiError} On API failure
 * 
 * @example
 * const result = await validateInvoiceDraft({
 *   invoice_type: 'freelancer',
 *   data: { business_name: 'Test', ... },
 *   skip_invoice_number: true
 * });
 */
export async function validateInvoiceDraft({ invoice_type, data, skip_invoice_number = true }) {
  const endpoint = API_ENDPOINTS.INVOICE_VALIDATE;

  try {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: getOptionalAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({ invoice_type, data, skip_invoice_number }),
      signal: AbortSignal.timeout(API_TIMEOUT),
    });
    
    await handleResponse(response, endpoint);
    return await response.json();
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(error.message, null, endpoint);
  }
}

/**
 * Calculate invoice totals and tax breakdown using backend logic
 * 
 * @param {Object} params - Calculation parameters
 * @param {string} params.invoice_type - Invoice type ID
 * @param {Object} params.data - Invoice data with line items
 * @returns {Promise<Object>} Calculation response: { valid, calculations, errors }
 * @throws {ApiError} On API failure
 * 
 * @example
 * const result = await calculateInvoice({
 *   invoice_type: 'freelancer',
 *   data: { line_items: [...], tax_rate: 10, ... }
 * });
 */
export async function calculateInvoice({ invoice_type, data }) {
  const endpoint = API_ENDPOINTS.INVOICE_CALCULATE;

  try {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: getOptionalAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({ invoice_type, data }),
      signal: AbortSignal.timeout(API_TIMEOUT),
    });
    
    await handleResponse(response, endpoint);
    return await response.json();
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(error.message, null, endpoint);
  }
}

/**
 * Finalize invoice and generate PDF (assigns invoice number atomically)
 * 
 * This is a tenant-scoped endpoint that requires Bearer token authentication.
 * 
 * @param {Object} params - Finalization parameters
 * @param {string} params.invoice_type - Invoice type ID
 * @param {Object} params.data - Complete invoice data
 * @returns {Promise<Object>} { blob, filename } if successful, or { valid: false, errors } if validation fails
 * @throws {ApiError} On API failure
 * 
 * @example
 * const result = await finalizeInvoice({
 *   invoice_type: 'freelancer',
 *   data: { business_name: 'Test', ... }
 * });
 * if (result.blob) {
 *   downloadPDF(result.blob, result.filename);
 * } else {
 *   console.error(result.errors);
 * }
 */
export async function finalizeInvoice({ invoice_type, data, draft_id = null }) {
  const endpoint = API_ENDPOINTS.INVOICE_FINALIZE;

  try {
    const body = { invoice_type, data };
    if (draft_id) body.draft_id = draft_id;
    const response = await authFetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(API_TIMEOUT),
    });

    await handleResponse(response, endpoint);

    // Check if response is PDF or validation error JSON
    const contentType = response.headers.get('content-type');
    
    if (contentType && contentType.includes('application/pdf')) {
      // Success - return PDF blob
      const blob = await response.blob();
      
      // Extract filename from Content-Disposition header
      const contentDisposition = response.headers.get('content-disposition');
      let filename = 'invoice.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) filename = filenameMatch[1];
      }
      
      return { blob, filename };
    } else {
      // Validation error - return JSON
      const errorData = await response.json();
      return errorData;
    }
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(error.message, null, endpoint);
  }
}

/**
 * Save or update an invoice draft without generating a PDF (Pro only).
 * @param {Object} params
 * @param {string} params.invoice_type
 * @param {Object} params.data
 * @param {number|null} params.draft_id - If provided, updates the existing draft
 * @returns {Promise<{id: number, created_at: string}>}
 */
export async function saveDraft({ invoice_type, data, draft_id = null }) {
  const endpoint = API_ENDPOINTS.INVOICE_DRAFT;
  try {
    const body = { invoice_type, data };
    if (draft_id) body.draft_id = draft_id;
    const response = await authFetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(API_TIMEOUT),
    });
    await handleResponse(response, endpoint);
    return await response.json();
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(error.message, null, endpoint);
  }
}

/**
 * List all saved drafts for the current user (Pro only).
 * @returns {Promise<{drafts: Array}>}
 */
export async function listDrafts() {
  const endpoint = API_ENDPOINTS.INVOICE_DRAFTS;
  try {
    const response = await authFetch(`${API_URL}${endpoint}`, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
      signal: AbortSignal.timeout(API_TIMEOUT),
    });
    await handleResponse(response, endpoint);
    return await response.json();
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(error.message, null, endpoint);
  }
}

/**
 * Delete a saved draft (Pro only).
 * @param {number} draftId
 */
export async function deleteDraft(draftId) {
  const endpoint = `${API_ENDPOINTS.INVOICE_DRAFTS}/${draftId}`;
  try {
    const response = await authFetch(`${API_URL}${endpoint}`, {
      method: 'DELETE',
      signal: AbortSignal.timeout(API_TIMEOUT),
    });
    await handleResponse(response, endpoint);
    return await response.json();
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(error.message, null, endpoint);
  }
}

/**
 * Generate a one-off guest invoice PDF — no auth required, not persisted.
 *
 * @param {Object} params
 * @param {string} params.invoice_type - Invoice type ID
 * @param {Object} params.data - Complete invoice data
 * @returns {Promise<Object>} { blob, filename } on success, or { valid: false, errors } on validation failure
 * @throws {ApiError} On API failure
 */
export async function generateGuestInvoice({ invoice_type, data }) {
  const endpoint = '/api/invoices/generate-guest';

  try {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ invoice_type, data }),
      signal: AbortSignal.timeout(API_TIMEOUT),
    });

    await handleResponse(response, endpoint);

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/pdf')) {
      const blob = await response.blob();
      const contentDisposition = response.headers.get('content-disposition');
      let filename = 'invoice.pdf';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+)"?/);
        if (match) filename = match[1];
      }
      return { blob, filename };
    } else {
      return await response.json();
    }
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(error.message, null, endpoint);
  }
}

// ============================================================================
// INVOICE HISTORY API (Feature #8)
// ============================================================================

/**
 * List all finalized invoices, newest first with pagination and optional filters.
 * 
 * This is a tenant-scoped endpoint that requires Bearer token authentication.
 * Returns only invoices belonging to the current tenant.
 * 
 * @param {Object} options - Query options
 * @param {number} options.limit - Maximum number of invoices to return (default: 20)
 * @param {number} options.offset - Number of invoices to skip (default: 0)
 * @param {string} [options.invoiceType] - Filter by invoice type (e.g., 'freelancer', 'gst')
 * @param {string} [options.q] - Search string for invoice number (case-insensitive partial match)
 * @returns {Promise<Object>} { invoices, total_count, limit, offset, schema_version }
 * @throws {ApiError} On API failure
 * 
 * @example
 * const { invoices, total_count } = await listInvoices({ limit: 20, offset: 0, invoiceType: 'freelancer' });
 * invoices.forEach(inv => console.log(inv.invoice_number, inv.total));
 */
export async function getInvoiceStats() {
  const endpoint = '/api/invoices/stats';
  try {
    const response = await authFetch(`${API_URL}${endpoint}`, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
      signal: AbortSignal.timeout(API_TIMEOUT),
    });
    await handleResponse(response, endpoint);
    return await response.json();
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(error.message, null, endpoint);
  }
}

export async function listInvoices({ limit = 20, offset = 0, invoiceType, q, paymentStatus, invoiceStatus } = {}) {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  params.set('offset', String(offset));

  if (invoiceType) {
    params.set('invoice_type', invoiceType);
  }
  if (q) {
    params.set('q', q);
  }
  if (paymentStatus) {
    params.set('payment_status', paymentStatus);
  }
  if (invoiceStatus) {
    params.set('invoice_status', invoiceStatus);
  }

  const endpoint = `/api/invoices?${params.toString()}`;
  
  try {
    const response = await authFetch(`${API_URL}${endpoint}`, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
      signal: AbortSignal.timeout(API_TIMEOUT),
    });

    await handleResponse(response, endpoint);
    return await response.json();
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(error.message, null, endpoint);
  }
}

/**
 * Get detailed information for a specific invoice.
 * 
 * This is a tenant-scoped endpoint that requires Bearer token authentication.
 * Returns 404 if the invoice doesn't exist or belongs to a different tenant.
 * 
 * @param {number} invoiceId - Database ID of the invoice record
 * @returns {Promise<Object>} Invoice detail with data and calculations
 * @throws {ApiError} On API failure or 404
 * 
 * @example
 * const invoice = await getInvoice(123);
 * console.log(invoice.invoice_number, invoice.calculations.total);
 */
export async function getInvoice(invoiceId) {
  const endpoint = `/api/invoices/${invoiceId}`;

  try {
    const response = await authFetch(`${API_URL}${endpoint}`, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
      signal: AbortSignal.timeout(API_TIMEOUT),
    });

    await handleResponse(response, endpoint);
    return await response.json();
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(error.message, null, endpoint);
  }
}

/**
 * Download the PDF for a specific invoice.
 * 
 * This is a tenant-scoped endpoint that requires Bearer token authentication.
 * Returns 404 if the invoice doesn't exist or belongs to a different tenant.
 * 
 * @param {number} invoiceId - Database ID of the invoice record
 * @returns {Promise<{blob: Blob, filename: string}>} PDF blob and filename
 * @throws {ApiError} On API failure or 404
 * 
 * @example
 * const { blob, filename } = await downloadInvoicePdf(123);
 * downloadPDF(blob, filename);
 */
export async function downloadInvoicePdf(invoiceId) {
  const endpoint = `/api/invoices/${invoiceId}/pdf`;

  try {
    const response = await authFetch(`${API_URL}${endpoint}`, {
      method: 'GET',
      signal: AbortSignal.timeout(API_TIMEOUT),
    });

    await handleResponse(response, endpoint);

    const blob = await response.blob();

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers.get('content-disposition');
    let filename = 'invoice.pdf';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
      if (filenameMatch) filename = filenameMatch[1];
    }

    return { blob, filename };
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(error.message, null, endpoint);
  }
}

/**
 * Export invoice history as CSV file.
 * 
 * This is a tenant-scoped endpoint that requires Bearer token authentication.
 * Exports all matching finalized invoices for the current tenant (ignoring pagination).
 * 
 * @param {Object} options - Filter options
 * @param {string} [options.invoiceType] - Filter by invoice type (e.g., 'freelancer', 'gst')
 * @param {string} [options.q] - Search string for invoice number (case-insensitive partial match)
 * @returns {Promise<{blob: Blob, filename: string}>} CSV blob and filename
 * @throws {ApiError} On API failure
 * 
 * @example
 * const { blob, filename } = await exportInvoicesCsv({ invoiceType: 'freelancer' });
 * downloadCSV(blob, filename);
 */
export async function exportInvoicesCsv({ invoiceType, q } = {}) {
  const params = new URLSearchParams();
  
  // Only add params if they have values
  if (invoiceType) {
    params.set('invoice_type', invoiceType);
  }
  if (q) {
    params.set('q', q);
  }
  
  const queryString = params.toString();
  const endpoint = `/api/invoices/export${queryString ? `?${queryString}` : ''}`;
  
  try {
    const response = await authFetch(`${API_URL}${endpoint}`, {
      method: 'GET',
      signal: AbortSignal.timeout(API_TIMEOUT),
    });

    await handleResponse(response, endpoint);

    const blob = await response.blob();

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers.get('content-disposition');
    let filename = 'invoices.csv';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      if (filenameMatch) filename = filenameMatch[1];
    }

    return { blob, filename };
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(error.message, null, endpoint);
  }
}

/**
 * Download a CSV blob as a file.
 * 
 * @param {Blob} blob - The CSV blob to download
 * @param {string} filename - The filename for the downloaded file
 */
export function downloadCSV(blob, filename = 'invoices.csv') {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Update an existing invoice with new data, keeping the same invoice number.
 *
 * @param {number} invoiceId - Database ID of the invoice to update
 * @param {Object} params
 * @param {string} params.invoice_type - Invoice type ID
 * @param {Object} params.data - Updated invoice data
 * @returns {Promise<Object>} { blob, filename } on success, or { valid: false, errors } on validation failure
 * @throws {ApiError} On API failure
 */
export async function updateInvoice(invoiceId, { invoice_type, data }) {
  const endpoint = `/api/invoices/${invoiceId}`;

  try {
    const response = await authFetch(`${API_URL}${endpoint}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ invoice_type, data }),
      signal: AbortSignal.timeout(API_TIMEOUT),
    });

    await handleResponse(response, endpoint);

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/pdf')) {
      const blob = await response.blob();
      const contentDisposition = response.headers.get('content-disposition');
      let filename = 'invoice.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) filename = filenameMatch[1];
      }
      return { blob, filename };
    } else {
      return await response.json();
    }
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(error.message, null, endpoint);
  }
}

// ============================================================================
// EMAIL
// ============================================================================

/**
 * Send an invoice PDF by email via Resend.
 * Requires an authenticated user (JWT token must be present).
 */
export async function sendInvoiceEmail({ invoice_type, data, to_email, cc_email, subject, message }) {
  const endpoint = '/api/email/send-invoice';
  try {
    const response = await authFetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ invoice_type, data, to_email, cc_email, subject, message }),
      signal: AbortSignal.timeout(30000),
    });
    await handleResponse(response, endpoint);
    return await response.json();
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(error.message, null, endpoint);
  }
}

export async function updateInvoiceStatus(invoiceId, paymentStatus) {
  const endpoint = `/api/invoices/${invoiceId}/status`;
  try {
    const response = await authFetch(`${API_URL}${endpoint}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ payment_status: paymentStatus }),
    });
    await handleResponse(response, endpoint);
    return await response.json();
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(error.message, null, endpoint);
  }
}

// ============================================================================
// BUSINESS PROFILES  (multi-profile support)
// ============================================================================

export async function listProfiles() {
  const endpoint = '/api/profiles';
  try {
    const response = await authFetch(`${API_URL}${endpoint}`);
    await handleResponse(response, endpoint);
    return await response.json(); // array of profile objects
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(error.message, null, endpoint);
  }
}

export async function createProfile(data) {
  const endpoint = '/api/profiles';
  try {
    const response = await authFetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    await handleResponse(response, endpoint);
    return await response.json();
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(error.message, null, endpoint);
  }
}

export async function updateProfile(id, data) {
  const endpoint = `/api/profiles/${id}`;
  try {
    const response = await authFetch(`${API_URL}${endpoint}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    await handleResponse(response, endpoint);
    return await response.json();
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(error.message, null, endpoint);
  }
}

export async function deleteProfile(id) {
  const endpoint = `/api/profiles/${id}`;
  try {
    const response = await authFetch(`${API_URL}${endpoint}`, { method: 'DELETE' });
    if (!response.ok && response.status !== 204) {
      await handleResponse(response, endpoint);
    }
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(error.message, null, endpoint);
  }
}

// ============================================================================
// CLIENT CONTACTS  (address book)
// ============================================================================

export async function listClients() {
  const endpoint = '/api/clients';
  try {
    const response = await authFetch(`${API_URL}${endpoint}`);
    await handleResponse(response, endpoint);
    return await response.json();
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(error.message, null, endpoint);
  }
}

export async function createClient(data) {
  const endpoint = '/api/clients';
  try {
    const response = await authFetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    await handleResponse(response, endpoint);
    return await response.json();
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(error.message, null, endpoint);
  }
}

export async function updateClient(id, data) {
  const endpoint = `/api/clients/${id}`;
  try {
    const response = await authFetch(`${API_URL}${endpoint}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    await handleResponse(response, endpoint);
    return await response.json();
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(error.message, null, endpoint);
  }
}

export async function deleteClient(id) {
  const endpoint = `/api/clients/${id}`;
  try {
    const response = await authFetch(`${API_URL}${endpoint}`, { method: 'DELETE' });
    if (!response.ok && response.status !== 204) {
      await handleResponse(response, endpoint);
    }
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(error.message, null, endpoint);
  }
}

// ============================================================================
// USER SETTINGS
// ============================================================================

export async function getSettings() {
  const endpoint = '/api/settings';
  try {
    const response = await authFetch(`${API_URL}${endpoint}`);
    await handleResponse(response, endpoint);
    return await response.json();
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(error.message, null, endpoint);
  }
}

export async function updateSettings(data) {
  const endpoint = '/api/settings';
  try {
    const response = await authFetch(`${API_URL}${endpoint}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    await handleResponse(response, endpoint);
    return await response.json();
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(error.message, null, endpoint);
  }
}

// ============================================================================
// BULK PDF DOWNLOAD
// ============================================================================

export async function bulkDownloadPdfs(invoiceIds) {
  const endpoint = '/api/invoices/bulk-pdf';
  try {
    const response = await authFetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ invoice_ids: invoiceIds }),
    });
    await handleResponse(response, endpoint);
    const blob = await response.blob();
    return { blob, filename: 'invoices.zip' };
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(error.message, null, endpoint);
  }
}

// ============================================================================
// GST SUMMARY
// ============================================================================

export async function getGstSummary(fy) {
  const endpoint = `/api/invoices/gst-summary?fy=${encodeURIComponent(fy)}`;
  try {
    const response = await authFetch(`${API_URL}${endpoint}`);
    await handleResponse(response, endpoint);
    return await response.json();
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(error.message, null, endpoint);
  }
}

// ============================================================================
// CSV IMPORT
// ============================================================================

export async function downloadImportTemplate() {
  const endpoint = '/api/invoices/import-template';
  try {
    const response = await authFetch(`${API_URL}${endpoint}`);
    await handleResponse(response, endpoint);
    const blob = await response.blob();
    return { blob, filename: 'invoice-import-template.csv' };
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(error.message, null, endpoint);
  }
}

export async function importInvoicesCsv(file) {
  const endpoint = '/api/invoices/import-csv';
  const formData = new FormData();
  formData.append('file', file);
  try {
    const response = await authFetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      body: formData,
    });
    await handleResponse(response, endpoint);
    return await response.json();
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(error.message, null, endpoint);
  }
}

// ============================================================================
// INVOICE SHARE
// ============================================================================

export async function shareInvoice(invoiceId) {
  const endpoint = `/api/invoices/${invoiceId}/share`;
  try {
    const response = await authFetch(`${API_URL}${endpoint}`, { method: 'POST' });
    await handleResponse(response, endpoint);
    return await response.json(); // { share_token }
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(error.message, null, endpoint);
  }
}

/** Public — no auth token required */
export async function getSharedInvoice(token) {
  const endpoint = `/api/share/${token}`;
  const response = await fetch(`${API_URL}${endpoint}`);
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new ApiError(body.detail || 'Invoice not found', response.status, endpoint);
  }
  return response.json();
}

/** Public — no auth token required, returns { blob, filename } */
export async function getSharedInvoicePdf(token) {
  const endpoint = `/api/share/${token}/pdf`;
  const response = await fetch(`${API_URL}${endpoint}`);
  if (!response.ok) throw new ApiError('PDF not available', response.status, endpoint);
  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="([^"]+)"/);
  return { blob, filename: match ? match[1] : 'invoice.pdf' };
}

// ============================================================================
// BILLING
// ============================================================================

/**
 * Create a Dodo Payments checkout session.
 * Returns { checkout_url } — frontend redirects the user there.
 */
export async function createCheckoutSession(plan) {
  const endpoint = '/api/billing/checkout';
  try {
    const response = await authFetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ plan }),
    });
    await handleResponse(response, endpoint);
    return response.json();
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(error.message, null, endpoint);
  }
}

/**
 * Cancel the current Pro subscription at the end of the billing period.
 * The user keeps Pro access until plan_expires_at.
 */
export async function cancelSubscription(reason = '') {
  const endpoint = '/api/billing/cancel';
  try {
    const response = await authFetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    });
    await handleResponse(response, endpoint);
    return await response.json();
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(error.message, null, endpoint);
  }
}

/**
 * Fetch the current billing status for the authenticated user.
 * Returns { plan, plan_interval, plan_expires_at, dodo_subscription_id, cancelled }
 */
export async function getBillingStatus() {
  const endpoint = '/api/billing/status';
  try {
    const response = await authFetch(`${API_URL}${endpoint}`);
    await handleResponse(response, endpoint);
    return response.json();
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(error.message, null, endpoint);
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

const apiExports = {
  fetchEntitlements,
  downloadPDF,
  downloadCSV,
  getTaxRates,
  getCurrencies,
  fetchInvoiceTypeConfig,
  fetchAllInvoiceTypes,
  pingBackend,
  validateInvoiceDraft,
  calculateInvoice,
  finalizeInvoice,
  generateGuestInvoice,
  updateInvoice,
  listInvoices,
  getInvoice,
  downloadInvoicePdf,
  exportInvoicesCsv,
  sendInvoiceEmail,
  updateInvoiceStatus,
  listProfiles,
  createProfile,
  updateProfile,
  deleteProfile,
  listClients,
  createClient,
  updateClient,
  deleteClient,
  shareInvoice,
  getSharedInvoice,
  getSharedInvoicePdf,
  getSettings,
  updateSettings,
  bulkDownloadPdfs,
  getGstSummary,
  downloadImportTemplate,
  importInvoicesCsv,
  createCheckoutSession,
  getBillingStatus,
  cancelSubscription,
  ApiError,
};

export default apiExports;